import { IsBoolean, IsInt, IsNumber, IsOptional, IsString, Max, Min } from 'class-validator';
import {
	MILLIONVERIFIER_MAX_COST_PER_CHECK,
	MILLIONVERIFIER_MAX_MONTHLY_CAP,
	MILLIONVERIFIER_MAX_WINDOW_DAYS,
	MILLIONVERIFIER_MIN_WINDOW_DAYS,
} from '../../constants/millionverifier';
import type { MillionVerifierConnectionEntity } from '../entity/millionverifier-connection.entity';
import type { MillionVerifierSpendEntity } from '../entity/millionverifier-spend.entity';

/** The settings an admin edits on the MillionVerifier page. */
export type MillionVerifierSettingsModel = Pick<
	MillionVerifierConnectionEntity,
	'windowDays' | 'recheckBeforeEachStep' | 'monthlyCapAmount' | 'costPerCheck'
>;

/** A month's checks: how many, what came back, what they cost. */
export type MillionVerifierMonthModel = Pick<
	MillionVerifierSpendEntity,
	'periodKey' | 'checkCount' | 'goodCount' | 'riskyCount' | 'badCount' | 'unknownCount' | 'cost'
>;

/**
 * PD-17610 — the MillionVerifier settings card and page. The API key is
 * never returned.
 */
export type MillionVerifierStatusDto =
	| { connected: false }
	| {
			connected: true;
			/** Read live from MillionVerifier; null when it could not be reached. */
			creditsLeft: number | null;
			/** Why the credits could not be read (a revoked key, MillionVerifier down). */
			creditsError: string | null;
			settings: MillionVerifierSettingsModel;
			thisMonth: MillionVerifierMonthModel;
			connectedDate: string | null;
			connectedByName: string | null;
	  };

/**
 * Connect (or reconnect) MillionVerifier. On reconnect a blank key keeps the
 * stored one; on a first connect the key is required (enforced in the
 * service).
 */
export class MillionVerifierConnectRequestDto {
	@IsOptional()
	@IsString()
	public apiKey?: string;

	@IsOptional()
	@IsNumber({ maxDecimalPlaces: 6 })
	@Min(0)
	@Max(MILLIONVERIFIER_MAX_COST_PER_CHECK)
	public costPerCheck?: number;
}

/** Save the "When Workd checks" settings. */
export class MillionVerifierSettingsRequestDto {
	@IsInt()
	@Min(MILLIONVERIFIER_MIN_WINDOW_DAYS)
	@Max(MILLIONVERIFIER_MAX_WINDOW_DAYS)
	public windowDays!: number;

	@IsBoolean()
	public recheckBeforeEachStep!: boolean;

	@IsNumber({ maxDecimalPlaces: 2 })
	@Min(0)
	@Max(MILLIONVERIFIER_MAX_MONTHLY_CAP)
	public monthlyCapAmount!: number;

	@IsOptional()
	@IsNumber({ maxDecimalPlaces: 6 })
	@Min(0)
	@Max(MILLIONVERIFIER_MAX_COST_PER_CHECK)
	public costPerCheck?: number;
}

/** "Test": the key still works, and the credits it has. */
export interface MillionVerifierTestResultDto {
	creditsLeft: number;
}
